import { useCallback, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DownloadFormat, ExportType } from '../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

const VALID_FORMATS = new Set<string>(Object.values(DownloadFormat));

function parseDownloadFormat(value: string | null): DownloadFormat | undefined {
  if (value && VALID_FORMATS.has(value)) return value as DownloadFormat;
  return undefined;
}

export interface ExportHandler {
  onDownload: (exportId: string, format?: DownloadFormat) => Promise<void>;
  /** Body text shown in the dialog. */
  message?: string;
}

interface ExportReadyDialogProps {
  /**
   * Map of `exportType` URL param → handler. Keys are constrained to the
   * shared `ExportType` enum so backend URL builders and frontend handlers
   * stay in sync. Missing keys are ignored unless `fallbackHandlerKey` is set.
   */
  handlers: Partial<Record<ExportType, ExportHandler>>;
  /**
   * Handler used when a link arrives with `exportId` but no `exportType`
   * (e.g. emails sent before the multi-export migration).
   * TODO: remove after 2 releases once legacy export emails have expired.
   */
  fallbackHandlerKey?: ExportType;
  /**
   * Optional title for the dialog.
   * @default "Download Ready"
   */
  dialogTitle?: string;
}

export function ExportReadyDialog({
  handlers,
  fallbackHandlerKey,
  dialogTitle = 'Download Ready',
}: ExportReadyDialogProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<DownloadFormat | undefined>();
  const [activeHandler, setActiveHandler] = useState<ExportHandler | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const exportIdParam = searchParams.get('exportId');
    if (!exportIdParam) return;

    const handlerKey = (searchParams.get('exportType') as ExportType | null) ?? fallbackHandlerKey;
    const handler = handlerKey ? handlers[handlerKey] : undefined;
    if (!handler) return;

    setExportId(exportIdParam);
    setExportFormat(parseDownloadFormat(searchParams.get('format')));
    setActiveHandler(handler);
    setIsOpen(true);

    // Strip the export-related params so they don't trigger again on reload.
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('exportId');
    newSearchParams.delete('format');
    newSearchParams.delete('exportType');
    newSearchParams.delete('name');
    setSearchParams(newSearchParams, { replace: true });
  }, []);

  const handleDownload = useCallback(async () => {
    if (!exportId || !activeHandler) return;

    setIsDownloading(true);
    try {
      await activeHandler.onDownload(exportId, exportFormat);
      setIsOpen(false);
      setExportId(null);
      setExportFormat(undefined);
      setActiveHandler(null);
    } catch (error) {
      console.error('Failed to download export:', error);
      toast.error('Failed to download export. Please try again, or contact support@wexler.ai');
    } finally {
      setIsDownloading(false);
    }
  }, [exportId, exportFormat, activeHandler]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setExportId(null);
    setActiveHandler(null);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" />
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <p>{activeHandler?.message ?? 'Your download is ready.'}</p>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleDownload} variant="primary" disabled={isDownloading}>
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
